import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Zap, RotateCcw } from "lucide-react";

const WIN_ROUNDS = 5;

export function ReactionDuel() {
  const [phase, setPhase] = useState("idle"); // idle | waiting | go | result
  const [scores, setScores] = useState({ p1: 0, p2: 0 });
  const [winner, setWinner] = useState(null);
  const [roundMsg, setRoundMsg] = useState("");
  const goTime = useRef(0);
  const timeout = useRef(null);
  const phaseRef = useRef("idle");

  const setPh = (p) => { phaseRef.current = p; setPhase(p); };

  const startRound = useCallback(() => {
    setWinner(null);
    setRoundMsg("");
    setPh("waiting");
    const delay = 1500 + Math.random() * 3000;
    timeout.current = setTimeout(() => {
      goTime.current = performance.now();
      setPh("go");
    }, delay);
  }, []);

  const reset = () => {
    clearTimeout(timeout.current);
    setScores({ p1: 0, p2: 0 });
    setWinner(null);
    setRoundMsg("");
    setPh("idle");
  };

  const handlePress = useCallback((player) => {
    const ph = phaseRef.current;
    if (ph === "waiting") {
      // too early -> other player gets the point
      clearTimeout(timeout.current);
      const other = player === "p1" ? "p2" : "p1";
      setRoundMsg(`${player === "p1" ? "Player 1" : "Player 2"} jumped early! Point to ${other === "p1" ? "Player 1" : "Player 2"}`);
      setScores((s) => {
        const ns = { ...s, [other]: s[other] + 1 };
        finalize(ns);
        return ns;
      });
      setPh("result");
    } else if (ph === "go") {
      const rt = Math.round(performance.now() - goTime.current);
      setRoundMsg(`${player === "p1" ? "Player 1" : "Player 2"} wins the round! (${rt}ms)`);
      setScores((s) => {
        const ns = { ...s, [player]: s[player] + 1 };
        finalize(ns);
        return ns;
      });
      setPh("result");
    }
  }, []);

  const finalize = (ns) => {
    if (ns.p1 >= WIN_ROUNDS) setWinner("p1");
    else if (ns.p2 >= WIN_ROUNDS) setWinner("p2");
  };

  useEffect(() => {
    const handler = (e) => {
      const k = e.key.toLowerCase();
      if (k === "q") { e.preventDefault(); handlePress("p1"); }
      if (k === "p") { e.preventDefault(); handlePress("p2"); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handlePress]);

  useEffect(() => () => clearTimeout(timeout.current), []);

  const bg = phase === "go" ? "bg-neon-lime" : phase === "waiting" ? "bg-neon-pink" : "bg-zinc-900";

  return (
    <div className="w-full max-w-2xl mx-auto" data-testid="reaction-duel">
      <div className="grid grid-cols-2 gap-4 mb-6">
        <PlayerCard label="Player 1" hint="Press Q" score={scores.p1} color="#00F0FF" onTap={() => handlePress("p1")} />
        <PlayerCard label="Player 2" hint="Press P" score={scores.p2} color="#FF003C" onTap={() => handlePress("p2")} />
      </div>

      <div className={`relative rounded-2xl border-2 border-zinc-700 ${bg} transition-colors duration-100 flex flex-col items-center justify-center text-center px-6`}
        style={{ minHeight: 220 }} data-testid="duel-arena">
        {phase === "idle" && (
          <>
            <Zap className="w-10 h-10 text-neon-yellow mb-3" />
            <p className="text-black font-display font-black text-xl uppercase max-w-sm" style={{ color: "#fff" }}>
              First to {WIN_ROUNDS} wins. When the box turns <span className="text-neon-lime">GREEN</span>, smash your key!
            </p>
            <Button onClick={startRound} data-testid="duel-start" className="mt-4 bg-neon-cyan text-black rounded-none font-bold uppercase">Start Duel</Button>
          </>
        )}
        {phase === "waiting" && <p className="text-black font-display font-black text-3xl uppercase">Wait for it…</p>}
        {phase === "go" && <p className="text-black font-display font-black text-5xl uppercase">GO!</p>}
        {phase === "result" && (
          <>
            <p className="text-white font-display font-black text-xl uppercase mb-2">{roundMsg}</p>
            {winner ? (
              <>
                <p className="text-neon-yellow font-display font-black text-3xl uppercase mb-3">
                  {winner === "p1" ? "Player 1" : "Player 2"} takes the match! 🏆
                </p>
                <Button onClick={reset} data-testid="duel-reset" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
                  <RotateCcw className="w-4 h-4 mr-1" /> New Match
                </Button>
              </>
            ) : (
              <Button onClick={startRound} data-testid="duel-next" className="bg-neon-cyan text-black rounded-none font-bold uppercase mt-2">Next Round</Button>
            )}
          </>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-4 text-center">
        Two players, one keyboard. P1 = <b className="text-neon-cyan">Q</b> · P2 = <b className="text-neon-pink">P</b> · or tap your panel on mobile.
      </p>
    </div>
  );
}

function PlayerCard({ label, hint, score, color, onTap }) {
  return (
    <button onClick={onTap} className="rounded-xl border-2 p-4 text-left transition-colors active:scale-95"
      style={{ borderColor: color, background: "rgba(24,24,27,0.6)" }} data-testid={`duel-${label.replace(" ", "-").toLowerCase()}`}>
      <p className="font-display font-bold uppercase text-sm" style={{ color }}>{label}</p>
      <p className="text-zinc-500 text-xs font-mono">{hint}</p>
      <p className="font-display font-black text-4xl mt-1" style={{ color }}>{score}</p>
    </button>
  );
}
